/**
 * Hook for checking seasonal availability
 * Requirements: 1.1, 1.3, 1.4, 1.5, 3.1
 */

import { useQuery } from "@tanstack/react-query";
import { getSeasonPeriod } from "../services/season.service";
import { isWithinSeason } from "../utils/seasonHelpers";
import type { SeasonPeriod } from "../types/season";

interface UseSeasonAvailabilityResult {
  isLoading: boolean;
  isWithinSeason: boolean;
  seasonPeriod: SeasonPeriod | null;
  error: Error | null;
}

/**
 * React hook to determine if the current date is within the active season
 *
 * - Fetches season configuration from API (Requirement 1.1)
 * - Returns isWithinSeason: true when current date is in season range (Requirement 1.3)
 * - Returns isWithinSeason: false when current date is outside season range (Requirement 1.4)
 * - Defaults to isWithinSeason: true on error to show booking form as fallback (Requirement 1.5)
 * - Returns isLoading state for loading indicator (Requirement 3.1)
 */
export function useSeasonAvailability(): UseSeasonAvailabilityResult {
  const {
    data: seasonPeriod,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["seasonPeriod"],
    queryFn: getSeasonPeriod,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Default to showing booking form (isWithinSeason: true) when:
  // - Still loading (will update once loaded)
  // - Error occurred
  // - No season period data returned (API failure or invalid data)
  if (isLoading) {
    return {
      isLoading: true,
      isWithinSeason: false, // Default while loading
      seasonPeriod: null,
      error: null,
    };
  }

  if (error || !seasonPeriod) {
    return {
      isLoading: false,
      isWithinSeason: false, // Fallback: show booking form on error
      seasonPeriod: null,
      error: error instanceof Error ? error : null,
    };
  }

  const currentDate = new Date();
  const withinSeason = isWithinSeason(
    currentDate,
    seasonPeriod.seasonStart,
    seasonPeriod.seasonEnd,
  );

  return {
    isLoading: false,
    isWithinSeason: withinSeason,
    seasonPeriod,
    error: null,
  };
}
